/* eslint-disable class-methods-use-this */
import get from 'lodash/get';
import cloneDeep from 'lodash/cloneDeep';
import every from 'lodash/every';
import extend from 'lodash/extend';
import find from 'lodash/find';
import isUndefined from 'lodash/isUndefined';
import merge from 'lodash/merge';
import { generateResultsPath } from '@AppRoutes/utils';
import history from '@AppRoutes/history';
import UserDatabases from '@Services/UserDatabases';
import { SearchTypes, RxSocketUrls } from '@Constants/CommonConstants';
import SimpleObservable from '@Utils/SimpleObservable';
import Breadcrumb from '@Services/Breadcrumb';
import * as Results from '@Services/Results';
import * as ResultsDataManage from '@Services/ResultsDataManage';
import ResultsInfo from '@Services/ResultsInfo';
import SearchStorage from '@Services/SearchStorage';
import { dispatchOpenFindSimilarModal } from '@Containers/Search/Shared/FindSimilar/FindSimilarModalActions';

export class SimilarityService {
  observer = new SimpleObservable();

  _searches = {};

  similarityModalWindow = null;

  _modalSearchId = null;

  /**
   * @name _getStateParams
   * @param result
   * @param searchId
   * @param databaseId
   * @param hitSetKey
   * @returns {Object} with state params
   * @private
   */
  _getStateParams = (result, searchId, databaseId, hitSetKey) => ({
    context: result.context,
    databaseId: result.databaseId || databaseId,
    resultName: result.resultName,
    hitSetKey: result.hitSetKey || hitSetKey,
    pageNumber: 1,
    sort: '',
    limitOrExclude: '',
    searchId,
  });

  /**
   * @name _startLoadingAlternativeResults
   * @param {Array} allResults all results coming from modal
   * @param {Object} selectedResult selected result coming from modal
   * @description forms alternative results and start loading them through resultsInfo service
   * @private
   */
  _startLoadingAlternativeResults = ({ allResults, selectedResult }) => {
    const alternativeResults = allResults
      .filter((item) => get(item, 'similarityValue') === selectedResult.similarityValue)
      .map((item) => {
        const defaultParams = {
          databaseId: item.databaseId,
          context: item.context,
          groupId: item.groupId,
          totalResult: item.resultSize,
        };

        return {
          ...defaultParams,
          queryId: item.queryId,
          resultName: item.resultName,
          hitSetKey: item.hitSetKey,
          searchId: item.searchId,
        };
      });

    ResultsInfo.startLoading(alternativeResults);
    return alternativeResults;
  };

  /**
   * @name _getSearch
   * @param searchId
   * @description get local search promise or search promise from SearchStorage
   * @private
   */
  _getSearch(searchId) {
    if (this._searches[searchId]) {
      return Promise.resolve(this._searches[searchId]);
    }

    return SearchStorage.get(searchId);
  }

  /**
   * @name _redirectToResults
   * @param stateParams
   * @description redirect to results with passed state params
   * @private
   */
  _redirectToResults = (stateParams) => {
    // eslint-disable-next-line no-param-reassign
    stateParams.limitOrExclude = '';
    const pathName = generateResultsPath(stateParams);
    history.push({
      pathname: pathName,
      state: stateParams,
    });
  };

  _getQBDatabasesOrder = (databasesQB) => {
    const orderedDatabases = [];

    UserDatabases.getAll().forEach((db) => {
      const index = databasesQB.indexOf(db.name);
      orderedDatabases[index] = db.id;
    });

    return orderedDatabases;
  };

  /**
   * @name _commitSearch
   * @param searchId
   * @param deleteLocalSource
   * @description commit local search into SearchStorage and delete local search if deleteLocalSource = true
   * @private
   */
  _commitSearch(searchId, deleteLocalSource) {
    return SearchStorage.extendItem(searchId, this._searches[searchId])
      .then((data) => {
        if (deleteLocalSource) {
          delete this._searches[searchId];
        }
        return data;
      })
      .catch(Logger.reject);
  }

  _createModalPromise = async (modalProps, onClose, onDismiss) => {
    this.similarityModalWindow = true;
    return new Promise((resolve) => {
      const onCloseCallback = (modalResult) => {
        resolve(modalResult);
        onClose(modalResult);
      };

      dispatchOpenFindSimilarModal({
        observer: this.observer,
        modalProps,
        closeCallback: onCloseCallback,
        dismissCallback: onDismiss,
      });
    });
  };

  _onModalDismiss() {
    this.similarityModalWindow = null;
  }

  _onModalWithSubscriptionDismiss(subscription) {
    subscription.unsubscribe();
    Results.cancelSearch(this._modalSearchId);
    this._modalSearchId = null;
    this.similarityModalWindow = null;
  }

  _updateModalResults(databaseId, data, updateLocalSearch = false) {
    if (!this._modalSearchId) {
      this._modalSearchId = get(data, 'payload.searchId', this._modalSearchId);
    }

    // eslint-disable-next-line no-param-reassign
    data.databaseId = databaseId;
    this.observer.addObject(data);

    if (updateLocalSearch) {
      this.updateLocalSearch(data, this._modalSearchId);
    }
  }

  async _onModalForDbSwitcherClose(similarityResult, modalResult) {
    const result = modalResult.selectedResult;
    const alternativeResults = this._startLoadingAlternativeResults(modalResult);

    await SearchStorage.set(result.searchId, {
      query: result.query,
      queryFactItem: result.queryString,
      searchType: SearchTypes.SIMILARITY_SEARCH,
      similarityResult,
      alternativeResultsDB: alternativeResults,
    })
      .then(() => {
        const stateParams = this._getStateParams(result, result.searchId);
        Breadcrumb.createBreadcrumb(stateParams, result.resultSize)
          .then(() => this._redirectToResults(stateParams))
          .catch(Logger.error);

        this._onModalDismiss();
      })
      .catch(Logger.error);

    return modalResult;
  }

  /**
   * @name showModalForDbSwitcher
   * @methodOf app.search.similarity:similarityService
   * @param {Object} similarityResult similarity data to initialize controller
   * @param {Object} currentDb object with current database data
   * @description shows modal containing similarity data (used in similarity mode via database switcher)
   */
  async showModalForDbSwitcher(similarityResult, currentDb) {
    if (this.similarityModalWindow) {
      return;
    }

    this.observer.clear();

    // Note: we can have not loaded similarity results in storage
    // So we need to get more completed result object
    await this._getSearch(similarityResult.searchId)
      .then((data) => {
        if (data) {
          this.observer.addObject({ payload: data.similarityResult });
        }
      })
      .catch(Logger.error);

    const onClose = (modalResult) => this._onModalForDbSwitcherClose(similarityResult, modalResult);
    const onDismiss = () => this._onModalDismiss();
    const modalProps = {
      currentDb,
    };
    // eslint-disable-next-line consistent-return
    return this._createModalPromise(modalProps, onClose, onDismiss);
  }

  async _onModalForQueryBuilderClose(searchId, data, modalResult) {
    if (!modalResult) {
      this._onModalDismiss();
      return null;
    }

    const result = modalResult.selectedResult;
    const alternativeResults = this._startLoadingAlternativeResults(modalResult);

    await SearchStorage.extendItem(searchId, {
      query: result.query,
      queryFactItem: result.queryFactItem,
      searchType: SearchTypes.SIMILARITY_SEARCH,
      similarityResult: data.payload,
      alternativeResultsDB: alternativeResults,
    })
      .then(() => {
        const stateParams = this._getStateParams(result, searchId);

        ResultsInfo.commitResultsToBreadcrumbs(result.groupId)
          .then(() => this._redirectToResults(stateParams))
          .catch(Logger.error);

        this._onModalDismiss();
      })
      .catch(Logger.error);
    return modalResult;
  }

  /**
   * @name showModalForQueryBuilder
   * @methodOf app.search.similarity:similarityService
   * @param {String} searchId id of current search in query builder
   * @param {Object} data object with initial data
   * @param {String} context of search
   * @param {Array} databasesQB array
   * @description shows similarity modal with initial data and data chunks which will come through observer (used
   * from Query Builder)
   */
  async showModalForQueryBuilder(searchId, data, context = null, databasesQB = []) {
    if (this.similarityModalWindow) {
      return;
    }

    this.observer.clear();

    const currentDb = { databaseId: data.databaseId };
    const databasesOrder = this._getQBDatabasesOrder(databasesQB);

    const onClose = (modalResult) => this._onModalForQueryBuilderClose(searchId, data, modalResult);
    const onDismiss = () => this._onModalDismiss();

    this.observer.addObject(data);

    if (context === 'reactions' || context === 'citations') {
      const modalProps = {
        currentDb,
      };
      // eslint-disable-next-line consistent-return
      return this._createModalPromise(modalProps, onClose, onDismiss);
    }
    const modalProps = {
      currentDb,
      databasesOrder,
      isIntegratedModal: true,
    };
    // eslint-disable-next-line consistent-return
    return this._createModalPromise(modalProps, onClose, onDismiss);
  }

  async _onModalForStructureClose(structure, subscription, modalResult) {
    if (!modalResult) {
      this._onModalWithSubscriptionDismiss(subscription);
      return null;
    }

    const result = modalResult.selectedResult;
    const alternativeResults = this._startLoadingAlternativeResults(modalResult);

    await this.updateSearch(this._modalSearchId, {
      query: result.query,
      queryFactItem: result.queryString,
      searchType: SearchTypes.SIMILARITY_SEARCH,
      structure,
      alternativeResultsDB: alternativeResults,
    });

    await this._commitSearch(this._modalSearchId)
      .then(() => {
        const stateParams = this._getStateParams(result, this._modalSearchId);
        Breadcrumb.createBreadcrumb(stateParams, result.resultSize)
          .then(() => this._redirectToResults(stateParams))
          .catch(Logger.error);

        this._onModalDismiss();
      })
      .catch(Logger.error);

    return modalResult;
  }

  /**
   * @name showModalForStructure
   * @methodOf app.search.similarity:similarityService
   * @param {Object} structure original structure
   * @param {String|undefined} [molStructure] mol file of structure for similar synthesis (used in alternative databases)
   * @param {String} databaseId current database id
   * @description requests similarity data for provided structure/mol file and shows modal based on result (used in
   * rxStructure to find similar structure and synthesize structure from additional databases)
   */
  async showModalForStructure(structure, molStructure, databaseId) {
    if (this.similarityModalWindow) {
      return;
    }
    this._modalSearchId = null;
    this.observer.clear();

    const channel = molStructure ? RxSocketUrls.SimilarSynthesize : RxSocketUrls.SimilarSubstanceAdvanced;
    const dbs = UserDatabases.getListWithIds();

    const observable = ResultsDataManage.getSimilar(structure.details.id, databaseId, molStructure, channel, dbs);
    const subscription = observable.subscribe({
      next: (data) => this._updateModalResults(databaseId, data, true),
      error: Logger.error,
    });

    const targetStructure = structure.molImage.structure || molStructure;

    const onClose = (modalResult) => {
      this._onModalForStructureClose(targetStructure, subscription, modalResult);
    };

    const onDismiss = () => this._onModalWithSubscriptionDismiss(subscription);
    const modalProps = {
      currentDb: { databaseId },
    };

    // eslint-disable-next-line consistent-return
    return this._createModalPromise(modalProps, onClose, onDismiss);
  }

  async _onModalForReactionClose(elementId, databaseId, hitSetKey, structure, subscription, modalResult) {
    if (!modalResult) {
      this._onModalWithSubscriptionDismiss(subscription);
      return null;
    }

    const result = modalResult.selectedResult;

    await SearchStorage.set(this._modalSearchId, {
      query: elementId,
      searchType: SearchTypes.SIMILARITY_SEARCH,
      structure,
    })
      .then(() => {
        const stateParams = this._getStateParams(result, this._modalSearchId, databaseId, hitSetKey);

        Breadcrumb.createBreadcrumb(stateParams, result.resultSize)
          .then(() => this._redirectToResults(stateParams))
          .catch(Logger.error);

        this._onModalWithSubscriptionDismiss(subscription);
      })
      .catch(Logger.error);

    return modalResult;
  }

  /**
   * @name showModalForReaction
   * @methodOf app.search.similarity:similarityService
   * @param {String} elementId element id for which is searching similar
   * @param {String} databaseId current database id
   * @param {String} hitSetKey current hit set key
   * @param {Object} structure structure to store for history description
   * @description requests similarity data for provided reaction id and shows modal with similar reactions
   */
  async showModalForReaction(elementId, databaseId, hitSetKey, structure) {
    if (this.similarityModalWindow) {
      return;
    }

    this.observer.clear();

    this._modalSearchId = null;

    const observable = ResultsDataManage.getSimilar(elementId, databaseId, null, RxSocketUrls.SimilarReactions);
    const subscription = observable.subscribe({
      next: (data) => this._updateModalResults(databaseId, data),
      error: Logger.error,
    });

    const onClose = (modalResult) => this._onModalForReactionClose(elementId, databaseId, hitSetKey, structure, subscription, modalResult);
    const onDismiss = () => this._onModalWithSubscriptionDismiss(subscription);
    const modalProps = {
      currentDb: { databaseId },
    };
    // eslint-disable-next-line consistent-return
    return this._createModalPromise(modalProps, onClose, onDismiss);
  }

  updateLocalSearch(data, searchId) {
    if (isUndefined(this._searches[searchId])) {
      const clonedPayload = cloneDeep(data.payload);
      this.updateSearch(searchId, { similarityResult: clonedPayload });
      return;
    }

    const similarityResultItems = this._searches[searchId].similarityResult.items;

    const itm = find(similarityResultItems, (item) => item.queryId === data.payload.queryId);

    if (itm) {
      extend(itm, data.payload);
    }

    const allLoaded = every(similarityResultItems, (item) => item.resultSize === 0 || !!item.resultSize);

    if (allLoaded && this._searches[searchId].searchType) {
      this._commitSearch(searchId);
    }
  }

  /**
   * @name updateSearch
   * @param searchId
   * @param data
   * @param isCommit
   * @description update local search and commit into SearchStorage if isCommit = true
   * @private
   */
  updateSearch(searchId, data, isCommit) {
    this._searches[searchId] = this._searches[searchId] || {};
    merge(this._searches[searchId], data);

    if (isCommit) {
      return this._commitSearch(searchId);
    }

    return Promise.resolve();
  }

  /**
   * @name updateObserver
   * @methodOf app.search.similarity:similarityService
   * @param {Object} data to pass it to observer
   * @description adds provided data to observer which will trigger all listeners on it
   */
  updateObserver(data) {
    this.observer.addObject(data);
  }
}

const Similarity = new SimilarityService();
export default Similarity;
